import {Button, Stack, Typography, useTheme} from '@mui/material';
import React from 'react';

import {TypeLocalNameEnum} from 'domain/usecases';
import {AppointmentBookingEnum} from 'main/analytics/gaTagsEnum';
import {HighlightedText} from 'presentation/components';
import Icon from 'presentation/icons';
import {useBookingStore} from 'presentation/modules/booking/shared/store/useBookingStore';
import {useMediaQueries} from 'presentation/pages/private/hooks';
import {translations} from 'presentation/translations-new';
import {formatText} from 'presentation/utils';
import {BookingTypeEnum, TELEMEDICINE_INFO} from '../../constants';
import {StyledCard, StyledStack} from './styles';
import {UnitCardProps} from './types';

const {select, selected, showDetails} = translations['pt-br'].commons;
const {cityReference, neighborhoodReference, telehealthAddress} =
	translations['pt-br'].privateFlow.bookingFlow.unitsFlow;

export function UnitCard({
	address,
	unitName,
	isTelemedicine,
	isSelected,
	onClick,
	query,
	isNotSelf,
	isFiltered,
	distanceReference,
	nmBairroOrigem,
}: UnitCardProps) {
	const theme = useTheme();
	const {isMobile} = useMediaQueries();
	const bookingData = useBookingStore(state => state.bookingData);
	const {typeLocalName, cityName, bookingType} = bookingData;

	const isExam = bookingType === BookingTypeEnum.EXAM;

	const buttonText = isSelected ? selected : select;
	const ctaText = isNotSelf ? showDetails : buttonText;
	const shouldHideDistanceInfo = isTelemedicine || !isFiltered;
	const dataTagId = (() => {
		if (isTelemedicine) {
			return AppointmentBookingEnum.SELECIONOU_TELECONSULTA;
		}
		if (isNotSelf) {
			return AppointmentBookingEnum.SELECIONOU_REDE_CREDENCIADA;
		}
		if (isFiltered) {
			return AppointmentBookingEnum.SELECIONOU_UNIDADE_POR_RAIO;
		}
		if (isExam) {
			return AppointmentBookingEnum.SELECIONOU_UNIDADE_EXAME;
		}
		return AppointmentBookingEnum.SELECIONOU_UNIDADE_POR_LOCAL;
	})();

	const distanceFilterTitle =
		typeLocalName === TypeLocalNameEnum.CIDADE
			? `${cityReference} ${formatText(cityName)}`
			: `${neighborhoodReference} ${formatText(nmBairroOrigem)}`;

	const shouldHideDistanceForTelemedicine = shouldHideDistanceInfo && isTelemedicine;
	const addressToDisplay = isTelemedicine ? telehealthAddress : address;

	return (
		<StyledCard
			$isSelected={isSelected}
			onClick={onClick}
			data-tag-id={dataTagId}
			elevation={0}
			sx={{
				backgroundColor: isTelemedicine ? theme.palette.primary.main : 'white',
				height: '100%',
			}}>
			<Stack
				gap={isMobile ? 2 : 1}
				height="100%"
				flexDirection={isMobile ? 'row' : 'column'}
				justifyContent={isMobile ? 'space-between' : undefined}
				alignItems={isMobile ? 'center' : undefined}>
				<Stack gap={1} height="100%">
					<StyledStack justifyContent="space-between">
						<Typography
							variant="subtitle1"
							fontWeight={600}
							lineHeight={1}
							color={isTelemedicine ? 'white' : 'primary'}>
							<HighlightedText
								text={
									isTelemedicine ? TELEMEDICINE_INFO.name : formatText(unitName)
								}
								highlight={query}
							/>
						</Typography>
					</StyledStack>
					<Stack mb="auto" gap={1} direction={shouldHideDistanceInfo ? 'row' : 'column'}>
						{shouldHideDistanceForTelemedicine ? (
							<Stack minWidth={20}>
								<Icon
									name={'remember-me-filled'}
									size={20}
									color={theme.palette.common.white}
								/>
							</Stack>
						) : null}

						<Typography
							variant="body2"
							color={isTelemedicine ? 'white' : 'text.secondary'}
							textOverflow="ellipsis"
							overflow="hidden">
							{addressToDisplay}
						</Typography>
						{isFiltered ? (
							<Stack direction="row" gap={0.5}>
								<Stack minWidth={20}>
									<Icon
										name={'location-on-filled'}
										size={20}
										color={theme.palette.primary.main}
									/>
								</Stack>
								<Typography variant="caption" fontWeight={600} color="primary">
									{distanceReference?.toFixed(1)}km {distanceFilterTitle}
								</Typography>
							</Stack>
						) : null}
					</Stack>
				</Stack>
				{isMobile ? (
					<Stack>
						<Icon
							name="arrow-right"
							size={24}
							color={
								isTelemedicine
									? theme.palette.other.progressBar
									: theme.colors.brand.primary.blue
							}
						/>
					</Stack>
				) : (
					<Button
						color={isSelected && !isTelemedicine ? 'success' : 'primary'}
						endIcon={
							isSelected ? (
								<Icon
									name="success"
									size={20}
									color={
										isTelemedicine ? theme.palette.other.progressBar : 'inherit'
									}
								/>
							) : null
						}
						variant={isSelected ? 'soft' : 'text'}
						sx={{alignSelf: 'flex-end', borderRadius: 0.5, p: 0}}>
						<Typography variant="button" color={isTelemedicine ? 'white' : 'inherit'}>
							{ctaText}
						</Typography>
					</Button>
				)}
			</Stack>
		</StyledCard>
	);
}
